import { DateRange } from '../../../shared/components/date-range-picker/date-range-picker.component';
import { findPeriodPreset, resolvePeriod } from '../../../shared/utils/period-presets';

// Restauração do período persistido do dashboard (localStorage). Função pura — a regra de
// re-ancoragem é sutil demais pra viver inline no componente sem teste.

export interface StoredDashboardPeriod {
  startDate?: string;
  endDate?: string;
  // Atalho do seletor ("last30", "thisMonth"...): com ele, as datas são recalculadas para hoje.
  preset?: string;
  // Dia (yyyy-MM-dd) em que a escolha foi salva — decide relativa vs. histórica.
  savedOn?: string;
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 86_400_000;

function toIsoDay(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * Janela que TERMINAVA no dia em que foi salva é tratada como relativa ("últimos N dias") e
 * re-ancorada em `today`; range histórico volta exato, clampado a hoje (e ao mínimo, se houver). Qualquer lixo
 * (JSON inválido, datas malformadas, range invertido) retorna null → chamador usa o default.
 */
export function restoreDashboardPeriod(
  raw: string | null,
  today: Date,
  minDate: Date | null = null,
): DateRange | null {
  try {
    if (!raw) return null;

    const saved = JSON.parse(raw) as StoredDashboardPeriod;
    if (
      !saved.startDate || !saved.endDate ||
      !ISO_DAY.test(saved.startDate) || !ISO_DAY.test(saved.endDate)
    )
      return null;

    const todayIso = toIsoDay(today);

    // Atalho: recalcula para hoje. "Este mês" continua sendo o mês corrente (a heurística abaixo o
    // trataria como "últimos N dias"). O fim não passa de hoje: a home não aceita data futura.
    if (findPeriodPreset(saved.preset)) {
      const resolved = resolvePeriod({ startDate: saved.startDate, endDate: saved.endDate, preset: saved.preset }, today);
      const end = resolved.endDate! > todayIso ? todayIso : resolved.endDate!;
      const minIso = minDate ? toIsoDay(minDate) : null;
      const start = minIso && resolved.startDate! < minIso ? minIso : resolved.startDate!;
      return start <= end ? { startDate: start, endDate: end, preset: saved.preset } : null;
    }

    if (saved.savedOn === saved.endDate) {
      const days = Math.round(
        (Date.parse(`${saved.endDate}T00:00:00`) - Date.parse(`${saved.startDate}T00:00:00`)) / DAY_MS,
      );
      if (days < 0) return null;
      const from = new Date(today);
      from.setDate(from.getDate() - days);
      return { startDate: toIsoDay(from), endDate: todayIso };
    }

    const minIso = minDate ? toIsoDay(minDate) : null;
    const start = minIso && saved.startDate < minIso ? minIso : saved.startDate;
    const end = saved.endDate > todayIso ? todayIso : saved.endDate;
    return start <= end ? { startDate: start, endDate: end } : null;
  } catch {
    return null;
  }
}
